"use client";

import { toast } from "@/app/_providers/toast";
import { defaultSupportAgentId } from "@/constants";
import { Button } from "@ui/components/core/Button";
import MicOffIcon from "@ui/icons/mic-off.svg";
import TranslateIcon from "@ui/central-icons/translate.svg";
import { useConversation } from "@elevenlabs/react";
import { handleMicrophoneError, getErrorMessageFromUnknownError } from "@utilities/errors";
import { logger } from "@utilities/logger";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { CenteredDiscLayout, HeroAgentCardLayout } from "./Components";
import { LanguageSelector } from "@ui/components/core/LanguageSelector";
import { convAILanguages, LanguageMetadata } from "@mw/shared/languagesAndAccents";
// TODO: migrate to react aria alternative
import { SelectTrigger } from "@radix-ui/react-select";
import { formatTime } from "@ui/lib/time";
import * as m from "motion/react-m";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import CallCancelIcon from "@ui/icons/CallCancelIcon";
import MicFilledIcon from "@ui/icons/MicFilledIcon";
import { OrbWithMessages } from "@ui/components/core/Orb/OrbWithMessages";

export interface ConversationViewProps {
  agentId?: string;
  onEndCall?: () => void;
  discLayoutId?: string;
}

export function ConversationView({ agentId = defaultSupportAgentId, onEndCall, discLayoutId }: ConversationViewProps) {
  const t = useTranslations("ClientKeys.hero_agent.views.conversation");
  const tMicrophoneErrors = useTranslations("ClientKeys.Errors.Microphone");

  // Local state
  const locale = useLocale();
  const [language, setSelectedLanguage] = useState<LanguageMetadata>(
    convAILanguages.find(l => l.value.startsWith(locale))!,
  );

  const [isMuted, setIsMuted] = useState(false);
  const [callStartTime, setCallStartTime] = useState<number | null>(null);
  const isCallStartingRef = useRef(false);
  // Pending language to switch to after disconnect
  const [pendingLanguage, setPendingLanguage] = useState<LanguageMetadata | null>(null);

  const { startSession, endSession, status } = useConversation({
    micMuted: isMuted,
    onError: error => {
      const err = error as unknown;
      logger.error(err instanceof Error ? err : null, "Conversation error");
      toast.error(t("error_connecting"));
    },
  });

  const startCall = useCallback(
    async (callLanguage: LanguageMetadata) => {
      if (isCallStartingRef.current) return;
      isCallStartingRef.current = true;

      try {
        await startSession({
          agentId,
          connectionType: "websocket",
          overrides: {
            agent: {
              language: callLanguage.value,
            },
          },
        });
        setCallStartTime(Date.now());
        captureEvent(EventName.mw_hero_agent_conversation_started, {
          agent_id: agentId,
          language: callLanguage.value,
        });
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to start session");

        captureEvent(EventName.mw_hero_agent_conversation_failed, {
          agent_id: agentId,
          error_message: getErrorMessageFromUnknownError(error),
        });

        if (error instanceof Error) {
          handleMicrophoneError(error, {
            handler: (title, description) => toast.error(title, { description }),
            t: tMicrophoneErrors,
            demoType: "hero_agent",
          });
        }

        onEndCall?.();
      } finally {
        isCallStartingRef.current = false;
      }
    },
    [agentId, startSession, tMicrophoneErrors, onEndCall],
  );

  const handleEndCallClick = useCallback(async () => {
    const durationSecs = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : null;
    captureEvent(EventName.mw_hero_agent_conversation_ended, {
      agent_id: agentId,
      call_duration_secs: durationSecs,
    });
    onEndCall?.();
    await endSession();
  }, [onEndCall, agentId, callStartTime, endSession]);

  const handleMuteToggle = useCallback(() => {
    setIsMuted(prev => {
      const newMutedState = !prev;
      captureEvent(EventName.mw_hero_agent_mute_toggled, {
        agent_id: agentId,
        is_muted: newMutedState,
      });
      return newMutedState;
    });
  }, [agentId]);

  // Start the call on mount
  useEffect(() => {
    startCall(language);
    return () => {
      endSession();
    };
    // Only run on mount/unmount, language changes are handled separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle reconnection after disconnect when there's a pending language change
  useEffect(() => {
    if (status === "disconnected" && pendingLanguage) {
      setSelectedLanguage(pendingLanguage);
      setPendingLanguage(null);
      startCall(pendingLanguage);
    }
  }, [status, pendingLanguage, startCall]);

  const handleLanguageChange = useCallback(
    (newLanguageValue: string) => {
      const fromLanguage = language.value;
      const newLanguage = convAILanguages.find(l => l.value === newLanguageValue)!;
      captureEvent(EventName.mw_hero_agent_language_changed, {
        agent_id: agentId,
        from_language: fromLanguage,
        to_language: newLanguageValue,
      });
      // Set pending language and disconnect - will reconnect when status becomes disconnected
      setPendingLanguage(newLanguage);
      endSession();
    },
    [agentId, language.value, endSession],
  );

  const isConnected = status === "connected";
  const isReconnecting = status === "disconnected" && pendingLanguage !== null;
  const statusText = isConnected
    ? null
    : status === "connecting" || isReconnecting
      ? t("connecting")
      : t("disconnected");

  return (
    <HeroAgentCardLayout width='lg' className='tw-flex tw-flex-col tw-justify-center tw-items-center'>
      {/* Timer */}
      <div className='tw-text-center tw-h-12'>
        <span className='tw-text-light tw-text-center tw-type-xs'>
          {statusText ?? <ConversationTimer startTime={callStartTime ?? 0} />}
        </span>
      </div>

      {/* Disc / Avatar */}
      <CenteredDiscLayout>
        <m.div layoutId={discLayoutId} className='tw-size-full'>
          <OrbWithMessages messages={[]} texture='agents-1' />
        </m.div>
      </CenteredDiscLayout>

      {/* Action Buttons */}
      <div className='tw-flex tw-items-center tw-justify-center tw-gap-4 sm:tw-gap-10'>
        <div className='tw-flex tw-flex-col tw-items-center tw-gap-3'>
          <LanguageSelector
            language={language}
            setSelectedLanguage={handleLanguageChange}
            sortedLanguages={convAILanguages}
            customSelectTrigger={() => {
              return (
                <SelectTrigger asChild>
                  <Button
                    variant='elevated'
                    size='pill-xl'
                    icon={<TranslateIcon className='tw-size-5' />}
                    disabled={!isConnected}
                  />
                </SelectTrigger>
              );
            }}
          />
          <span className='tw-text-gray-500 tw-type-xs tw-hidden md:tw-block'>{t("languages")}</span>
        </div>
        <div className='tw-flex tw-flex-col tw-items-center tw-gap-3'>
          <Button
            variant={isMuted ? "primary" : "elevated"}
            size='pill-xl'
            icon={
              isMuted ? <MicOffIcon className='tw-size-5' /> : <MicFilledIcon className='tw-text-black tw-size-5' />
            }
            onClick={handleMuteToggle}
            disabled={!isConnected}
          />
          <span className='tw-text-gray-500 tw-type-xs tw-hidden md:tw-block'>{isMuted ? t("unmute") : t("mute")}</span>
        </div>
        <div className='tw-flex tw-flex-col tw-items-center tw-gap-3'>
          <Button
            variant='elevated'
            size='pill-xl'
            icon={<CallCancelIcon className='tw-text-black tw-size-5' />}
            onClick={handleEndCallClick}
            disabled={!isConnected}
          />
          <span className='tw-text-gray-500 tw-type-xs tw-hidden md:tw-block'>{t("end_call")}</span>
        </div>
      </div>
    </HeroAgentCardLayout>
  );
}

function ConversationTimer({ startTime }: { startTime: number }) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const updateTime = () => {
      if (!startTime) return;
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    };
    intervalRef.current = setInterval(updateTime, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startTime]);
  return <span>{formatTime(elapsed)}</span>;
}
